// Negociações (specs/21): quadro por etapa, cobrança do que está parado e passos sem precisar de contato.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Toast, useToast } from '../components/ui';
import { data, hojeISO, moeda, num } from '../format';
import { COR_GANHO, COR_PERDIDO, type EtapaCfg } from '../negociacao';
import { useUsuarios } from './Clientes';

type Periodo = 'mes' | 'anterior' | '90d';
interface Neg {
  id: string; titulo: string; status: 'ABERTA' | 'GANHA' | 'PERDIDA'; etapaId: string; valor: string; quantidade: number | null;
  prazoEm: string | null; motivoPerda: string | null; criadaEm: string; fechadaEm: string | null; vencida: boolean; naEtapaDesde: string | null;
  clienteId: string; cliente: string; empresa: string | null; responsavelId: string; responsavel: string;
}
interface Resp {
  ativa: boolean; etapas: EtapaCfg[]; ganho: { id: string; nome: string } | null; perdido: { id: string; nome: string } | null;
  motivos: Array<{ id: string; nome: string }>; abertas: Neg[]; fechadas: Neg[];
}
interface Passo { quando: string; etapa: string; tipo: string; valor: string | null; quantidade: number | null; prazoEm: string | null; motivo: string | null; observacao: string | null; por: string | null; canal: string | null }

const ROTULO: Record<Periodo, string> = { mes: 'Este mês', anterior: 'Mês passado', '90d': '90 dias' };
const mil = (v: number) => (v >= 1000 ? `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil` : moeda(v));
const iniciais = (s: string) => s.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
const diasDesde = (iso: string | null) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)) : 0);
const quantos = (n: number) => (n === 0 ? 'hoje' : n === 1 ? 'há 1 dia' : `há ${n} dias`);
const unidade = (e: EtapaCfg | undefined) => (e?.rotuloQuantidade ?? 'unidades').toLowerCase();

export function NegociacoesPage() {
  const { pode, me } = useAuth();
  const admin = pode('RELATORIOS_TENANT');
  const usuarios = useUsuarios(admin);
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [usuarioId, setUsuarioId] = useState('');
  const [soCobrar, setSoCobrar] = useState(false);
  const [sel, setSel] = useState<string | null>(null);
  // arrastar cartões entre colunas
  const qc = useQueryClient();
  const toast = useToast();
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [sobre, setSobre] = useState<string | null>(null);
  const [acaoInicial, setAcaoInicial] = useState<Acao | null>(null);
  const [movendo, setMovendo] = useState(false);
  const qs = new URLSearchParams({ periodo });
  if (usuarioId) qs.set('usuarioId', usuarioId);
  const q = useQuery({ queryKey: ['negociacoes', qs.toString()], queryFn: () => api<Resp>(`/negociacoes?${qs}`), placeholderData: (p) => p });

  if (q.isLoading) return <Carregando />;
  if (q.error) return <Erro erro={mensagemErro(q.error)} />;
  const d = q.data!;
  if (!d.ativa) {
    return (
      <>
        <h1>Negociações</h1>
        <div className="empty">O módulo de negociação está desligado.{pode('CONFIGURACOES_GERENCIAR') && <> Ligue em <Link to="/configuracoes/negociacao">Configurações › Negociação</Link>.</>}</div>
      </>
    );
  }
  const etapaDe = (id: string) => d.etapas.find((e) => e.id === id);
  const precisaCobrar = (n: Neg) => n.vencida;
  const soma = (xs: Neg[]) => xs.reduce((a, n) => a + Number(n.valor), 0);
  const ganhas = d.fechadas.filter((n) => n.status === 'GANHA');
  const perdidas = d.fechadas.filter((n) => n.status === 'PERDIDA');
  const pipeline = d.abertas.filter((n) => etapaDe(n.etapaId)?.somaPipeline);
  const motivos = new Map<string, number>();
  for (const n of perdidas) motivos.set(n.motivoPerda ?? '—', (motivos.get(n.motivoPerda ?? '—') ?? 0) + Number(n.valor));
  const topMotivo = [...motivos.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  const conv = soma(ganhas) + soma(perdidas) > 0 ? Math.round((soma(ganhas) / (soma(ganhas) + soma(perdidas))) * 100) : null;
  const cobrar = d.abertas.filter(precisaCobrar);
  const selecionada = [...d.abertas, ...d.fechadas].find((n) => n.id === sel) ?? null;

  const arrastada = d.abertas.find((n) => n.id === arrastando);
  const podeSoltar = (colId: string) => !!arrastada && !movendo && colId !== arrastada.etapaId;
  /** Soltou o cartão numa coluna: move direto quando a etapa não pede nada novo; senão abre o painel já na ação. */
  async function soltar(colId: string) {
    const n = arrastada;
    setArrastando(null);
    setSobre(null);
    if (!n || colId === n.etapaId) return;
    const abrirPainel = (a: Acao) => { setAcaoInicial(a); setSel(n.id); };
    if (colId === 'G') return abrirPainel({ destino: 'GANHO' });
    if (colId === 'P') return abrirPainel({ destino: 'PERDIDO' });
    const e = etapaDe(colId);
    if (!e) return;
    if ((e.pedeQuantidade && n.quantidade == null) || (e.pedeValor === 'OBRIGATORIO' && !Number(n.valor))) {
      return abrirPainel({ destino: 'ETAPA', etapaId: colId });
    }
    setMovendo(true);
    try {
      await api(`/oportunidades/${n.id}/passo`, {
        body: {
          destino: 'ETAPA', etapaId: colId,
          ...(e.pedeQuantidade && n.quantidade != null ? { quantidade: n.quantidade } : {}),
          ...(e.pedeValor !== 'NAO' && Number(n.valor) ? { valor: Number(n.valor) } : {}),
        },
      });
      await Promise.all([qc.invalidateQueries({ queryKey: ['negociacoes'] }), qc.invalidateQueries({ queryKey: ['dashboard'] }), qc.invalidateQueries({ queryKey: ['cliente'] })]);
      toast.show(`${n.cliente} movido para ${e.nome}.`);
    } catch (err) {
      toast.show(mensagemErro(err));
    } finally {
      setMovendo(false);
    }
  }

  const colunas = [
    ...d.etapas.map((e) => {
      const xs = d.abertas.filter((n) => n.etapaId === e.id && (!soCobrar || precisaCobrar(n)));
      const todos = d.abertas.filter((n) => n.etapaId === e.id);
      const qtd = todos.reduce((a, n) => a + (n.quantidade ?? 0), 0);
      const atras = todos.filter(precisaCobrar).length;
      return {
        id: e.id, nome: e.nome, cor: e.cor, xs,
        total: e.pedeValor !== 'NAO' ? mil(soma(todos)) : `${num(todos.length)} ${todos.length === 1 ? 'aberta' : 'abertas'}`,
        sub: [e.pedeQuantidade ? `${num(qtd)} ${unidade(e)}` : null, e.pedeValor !== 'NAO' ? `${num(todos.length)} em aberto` : null,
          atras ? `${atras} ${e.pedeValor === 'NAO' ? 'sem retorno' : 'vencidos'}` : null].filter(Boolean).join(' · ') || 'em aberto',
      };
    }),
    { id: 'G', nome: d.ganho?.nome ?? 'Ganho', cor: COR_GANHO, xs: soCobrar ? [] : ganhas, total: mil(soma(ganhas)), sub: `${ROTULO[periodo].toLowerCase()} · entra em vendas` },
    { id: 'P', nome: d.perdido?.nome ?? 'Perdido', cor: COR_PERDIDO, xs: soCobrar ? [] : perdidas, total: mil(soma(perdidas)), sub: `${ROTULO[periodo].toLowerCase()} · não entra em vendas` },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Negociações</h1>
          <span className="muted small">{admin ? 'Toda a equipe' : `Suas negociações, ${me?.usuario.nome.split(' ')[0] ?? ''}`} · abertas agora e o que fechou no período</span>
        </div>
        <div className="actions">
          {admin && (
            <select className="select" value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)} aria-label="Vendedor">
              <option value="">Todos os vendedores</option>
              {usuarios.data?.filter((u) => u.status === 'ATIVO').map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
          )}
          <div className="seg" role="group" aria-label="Período dos fechados">
            {(['mes', 'anterior', '90d'] as const).map((p) => <button key={p} type="button" aria-pressed={periodo === p} onClick={() => setPeriodo(p)}>{ROTULO[p]}</button>)}
          </div>
        </div>
      </div>

      <div className="grid-kpi" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <div className="card kpi"><span className="label">Em aberto</span><span className="value num">{num(d.abertas.length)}</span>
          <span className="small muted">{d.etapas.map((e) => `${d.abertas.filter((n) => n.etapaId === e.id).length} ${e.nome.toLowerCase()}`).join(' · ')}</span></div>
        <div className="card kpi"><span className="label">R$ em aberto (pipeline)</span><span className="value num" style={{ color: '#fde68a' }}>{mil(soma(pipeline))}</span>
          <span className="small muted">{num(pipeline.length)} com valor</span></div>
        <div className="card kpi"><span className="label">{d.ganho?.nome ?? 'Ganho'} · {ROTULO[periodo].toLowerCase()}</span><span className="value num" style={{ color: 'var(--green-text)' }}>{mil(soma(ganhas))}</span>
          <span className="small muted">{num(ganhas.length)} vendas</span></div>
        <div className="card kpi"><span className="label">{d.perdido?.nome ?? 'Perdido'} · {ROTULO[periodo].toLowerCase()}</span><span className="value num" style={{ color: 'var(--red-text)' }}>{mil(soma(perdidas))}</span>
          <span className="small muted">{perdidas.length ? `${num(perdidas.length)} · mais por ${topMotivo}` : 'nenhuma perda'}</span></div>
        <div className="card kpi"><span className="label">Conversão</span><span className="value num">{conv === null ? '—' : `${conv}%`}</span>
          <span className="small muted">do valor fechado virou venda</span></div>
      </div>

      {cobrar.length > 0 && (
        <div className="alert warn" style={{ alignItems: 'center' }}>
          <Icon name="alerta" />
          <span>{d.etapas.map((e) => {
            const n = cobrar.filter((x) => x.etapaId === e.id).length;
            return n ? `${n} em ${e.nome.toLowerCase()} ${e.pedeValor === 'NAO' ? 'sem retorno' : 'vencidos'}` : null;
          }).filter(Boolean).join(' · ')}</span>
          <label className="check" style={{ marginLeft: 'auto' }}><input type="checkbox" checked={soCobrar} onChange={(e) => setSoCobrar(e.target.checked)} />Mostrar só o que precisa de cobrança</label>
        </div>
      )}

      <span className="small faint">Arraste um cartão para outra coluna para mover a negociação. {d.ganho?.nome ?? 'Ganho'} e {d.perdido?.nome ?? 'Perdido'} pedem confirmação; etapas que pedem algo novo abrem o painel para preencher.</span>
      <div className={`quadro-neg${movendo ? ' movendo' : ''}`}>
        {colunas.map((c) => (
          <section key={c.id} aria-label={c.nome}
            className={`coluna-neg${arrastando && podeSoltar(c.id) ? ' pode-soltar' : ''}${sobre === c.id ? ' alvo' : ''}`}
            onDragOver={(ev) => {
              if (!podeSoltar(c.id)) return;
              ev.preventDefault();
              ev.dataTransfer.dropEffect = 'move';
              if (sobre !== c.id) setSobre(c.id);
            }}
            onDragLeave={(ev) => { if (!ev.currentTarget.contains(ev.relatedTarget as Node | null)) setSobre((x) => (x === c.id ? null : x)); }}
            onDrop={(ev) => { ev.preventDefault(); void soltar(c.id); }}>
            <div className="coluna-neg-h">
              <span className="row" style={{ gap: 8, alignItems: 'center', fontWeight: 600, fontSize: 13.5 }}>
                <span className="status-dot" style={{ background: c.cor, width: 9, height: 9 }} aria-hidden="true" />{c.nome}
                <span className="num faint" style={{ fontWeight: 400 }}>{c.xs.length}</span>
              </span>
              <span className="num" style={{ fontSize: 18, fontWeight: 600, color: c.cor }}>{c.total}</span>
              <span className="small faint">{c.sub}</span>
            </div>
            <div className="coluna-neg-b">
              {c.xs.map((n) => {
                const e = etapaDe(n.etapaId);
                const valor = n.status === 'ABERTA' && e && e.pedeValor === 'NAO'
                  ? (e.pedeQuantidade ? `${num(n.quantidade ?? 0)} ${unidade(e)}` : '—')
                  : moeda(n.valor);
                const quando = n.status === 'ABERTA'
                  ? `${quantos(diasDesde(n.naEtapaDesde ?? n.criadaEm))}${n.prazoEm && !n.vencida ? ` · até ${data(n.prazoEm).slice(0, 5)}` : ''}`
                  : `em ${n.fechadaEm ? new Date(n.fechadaEm).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—'}`;
                return (
                  <button key={n.id} type="button" className={`cartao-neg${arrastando === n.id ? ' arrastando' : ''}`} aria-pressed={sel === n.id}
                    draggable={n.status === 'ABERTA' && !movendo}
                    onDragStart={(ev) => { ev.dataTransfer.setData('text/plain', n.id); ev.dataTransfer.effectAllowed = 'move'; setArrastando(n.id); }}
                    onDragEnd={() => { setArrastando(null); setSobre(null); }}
                    onClick={() => { setAcaoInicial(null); setSel(n.id); }}>
                    <span className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'nowrap', alignItems: 'baseline' }}>
                      <strong style={{ fontWeight: 500, fontSize: 13.5, minWidth: 0 }}>{n.cliente}</strong>
                      <span className="num small" style={{ whiteSpace: 'nowrap' }}>{valor}</span>
                    </span>
                    <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                      <span className="av sm">{iniciais(n.responsavel)}</span>
                      <span className="small faint">{quando}</span>
                      {n.vencida && <span className="badge amber">{e?.pedeValor === 'NAO' ? 'sem retorno' : `vencido ${data(n.prazoEm).slice(0, 5)}`}</span>}
                      {n.status === 'PERDIDA' && n.motivoPerda && <span className="badge">{n.motivoPerda}</span>}
                    </span>
                  </button>
                );
              })}
              {!c.xs.length && <span className="small faint" style={{ padding: 8 }}>Nada aqui.</span>}
            </div>
          </section>
        ))}
      </div>

      <Toast msg={toast.msg} onDone={toast.clear} />
      {selecionada && (
        <PainelNegociacao key={`${selecionada.id}-${acaoInicial?.destino ?? ''}-${acaoInicial?.etapaId ?? ''}`}
          neg={selecionada} cfg={d} acaoInicial={acaoInicial} onClose={() => { setSel(null); setAcaoInicial(null); }} />
      )}
    </>
  );
}

type Acao = { destino: 'ETAPA' | 'GANHO' | 'PERDIDO'; etapaId?: string };

function PainelNegociacao({ neg, cfg, acaoInicial, onClose }: { neg: Neg; cfg: Resp; acaoInicial?: Acao | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const hist = useQuery({ queryKey: ['negociacao-historico', neg.id], queryFn: () => api<Passo[]>(`/oportunidades/${neg.id}/historico`) });
  const [acao, setAcao] = useState<Acao | null>(null);
  const [valor, setValor] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [dataVenda, setDataVenda] = useState(hojeISO());
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const aberta = neg.status === 'ABERTA';
  const etapa = cfg.etapas.find((e) => e.id === neg.etapaId);
  const alvo = acao?.destino === 'ETAPA' ? cfg.etapas.find((e) => e.id === acao.etapaId) : undefined;
  const corDe = (p: Passo) => (p.tipo === 'GANHO' ? COR_GANHO : p.tipo === 'PERDIDO' ? COR_PERDIDO : cfg.etapas.find((e) => e.nome === p.etapa)?.cor ?? '#94A3B8');

  function abrir(a: NonNullable<typeof acao>) {
    setAcao(a);
    setErro(null);
    setValor(Number(neg.valor) ? Number(neg.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '');
    setQuantidade(neg.quantidade != null ? String(neg.quantidade) : '');
    setMotivo('');
  }
  // veio de um cartão arrastado: já abre na ação, com os valores da negociação
  useEffect(() => { if (acaoInicial) abrir(acaoInicial); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function confirmar() {
    if (!acao) return;
    setErro(null);
    const v = Number(valor.replace(/\./g, '').replace(',', '.')) || undefined;
    try {
      await api(`/oportunidades/${neg.id}/passo`, {
        body: {
          destino: acao.destino, ...(acao.etapaId ? { etapaId: acao.etapaId } : {}),
          ...(v !== undefined ? { valor: v } : {}), ...(quantidade && alvo?.pedeQuantidade ? { quantidade: Number(quantidade) } : {}),
          ...(acao.destino === 'GANHO' ? { data: dataVenda } : {}), ...(acao.destino === 'PERDIDO' ? { motivo } : {}),
        },
      });
      await Promise.all([qc.invalidateQueries({ queryKey: ['negociacoes'] }), qc.invalidateQueries({ queryKey: ['negociacao-historico', neg.id] }),
        qc.invalidateQueries({ queryKey: ['clientes'] }), qc.invalidateQueries({ queryKey: ['dashboard'] })]);
      toast.show(acao.destino === 'GANHO' ? 'Venda registrada.' : acao.destino === 'PERDIDO' ? 'Negociação marcada como perdida.' : `Movida para ${alvo?.nome}.`);
      setAcao(null);
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  return (
    <div className="overlay drawer-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={`Negociação de ${neg.cliente}`} style={{ maxWidth: 480 }}>
        <header className="drawer-head">
          <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'nowrap' }}>
            <h2 style={{ fontSize: 18 }}>{neg.cliente}</h2>
            <button className="btn sm icon-btn" style={{ marginLeft: 'auto' }} aria-label="Fechar" onClick={onClose}><Icon name="fechar" size={14} /></button>
          </div>
          <span className="small muted">{neg.empresa ? `${neg.empresa} · ` : ''}carteira de {neg.responsavel}</span>
          <span className="row" style={{ gap: 10, alignItems: 'baseline' }}>
            <span className="num" style={{ fontSize: 22, fontWeight: 600 }}>{etapa?.pedeValor === 'NAO' && aberta ? (etapa.pedeQuantidade ? `${num(neg.quantidade ?? 0)} ${unidade(etapa)}` : '—') : moeda(neg.valor)}</span>
            <span className="small faint">{aberta ? `em ${etapa?.nome.toLowerCase() ?? 'aberto'}${neg.prazoEm ? ` · até ${data(neg.prazoEm)}` : ''}` : neg.status === 'GANHA' ? (cfg.ganho?.nome ?? 'ganho') : `${cfg.perdido?.nome ?? 'perdido'} · ${neg.motivoPerda ?? ''}`}</span>
          </span>
        </header>
        <div className="drawer-body" style={{ gap: 18 }}>
          <div className="col" style={{ gap: 6 }}>
            <span className="rotulo-neg">Caminho</span>
            {hist.isLoading ? <Carregando /> : hist.error ? <Erro erro={mensagemErro(hist.error)} /> : (
              <ol className="trilha-status">
                {hist.data!.map((p, i) => (
                  <li key={i}>
                    <span className="num small faint">{new Date(p.quando).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                    <span className="pt" style={{ background: corDe(p) }} aria-hidden="true" />
                    <span className="small"><strong style={{ fontWeight: 500 }}>{p.etapa}</strong>
                      <span className="faint">{[p.quantidade != null ? `${num(p.quantidade)} un.` : null, p.valor ? moeda(p.valor) : null, p.motivo, p.canal ? p.canal.toLowerCase() : null, p.por].filter(Boolean).map((x) => ` · ${x}`).join('')}</span>
                      {p.observacao && <><br /><span className="muted">{p.observacao}</span></>}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
          {aberta && (
            <div className="col" style={{ gap: 10 }}>
              <span className="rotulo-neg">Atualizar</span>
              <div className="row" style={{ gap: 8 }}>
                {cfg.etapas.filter((e) => e.id !== neg.etapaId).map((e) => (
                  <button key={e.id} className="btn sm" aria-pressed={acao?.etapaId === e.id} onClick={() => abrir({ destino: 'ETAPA', etapaId: e.id })}>Mover para {e.nome}</button>
                ))}
                <button className="btn sm venda" onClick={() => abrir({ destino: 'GANHO' })}>{cfg.ganho?.nome ?? 'Ganhou'}</button>
                <button className="btn sm" onClick={() => abrir({ destino: 'PERDIDO' })}>{cfg.perdido?.nome ?? 'Perdeu'}…</button>
              </div>
              {acao && (
                <div className="venda-box on" style={acao.destino === 'PERDIDO' ? { borderColor: '#5b2328', background: '#1a0e10' } : acao.destino === 'ETAPA' ? { borderColor: 'var(--border-2)', background: 'var(--bg-side)' } : undefined}>
                  <Erro erro={erro} />
                  {acao.destino === 'ETAPA' && alvo?.pedeQuantidade && (
                    <label className="field"><span>{alvo.rotuloQuantidade ?? 'Quantidade'} *</span>
                      <input className="input num" inputMode="numeric" value={quantidade} onChange={(e) => setQuantidade(e.target.value.replace(/\D/g, ''))} /></label>
                  )}
                  {(acao.destino !== 'ETAPA' || (alvo && alvo.pedeValor !== 'NAO')) && (
                    <label className="field"><span>{acao.destino === 'GANHO' ? 'Valor vendido *' : acao.destino === 'PERDIDO' ? 'Valor que deixou de vender' : `${alvo?.rotuloValor ?? 'Valor'}${alvo?.pedeValor === 'OBRIGATORIO' ? ' *' : ''}`}</span>
                      <span className="input-prefixo"><span aria-hidden="true">R$</span><input className="input num" inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value.replace(/[^\d.,]/g, ''))} /></span></label>
                  )}
                  {acao.destino === 'GANHO' && (
                    <label className="field"><span>Data da venda *</span><input className="input num" type="date" max={hojeISO()} value={dataVenda} onChange={(e) => setDataVenda(e.target.value)} /></label>
                  )}
                  {acao.destino === 'PERDIDO' && (
                    <div className="field"><span>Motivo *</span>
                      <div className="row" style={{ gap: 6 }}>
                        {cfg.motivos.map((m) => <button key={m.id} type="button" className={`pill${motivo === m.nome ? ' on' : ''}`} aria-pressed={motivo === m.nome} onClick={() => setMotivo(m.nome)}>{m.nome}</button>)}
                      </div>
                    </div>
                  )}
                  <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
                    <button className="btn sm" onClick={() => setAcao(null)}>Cancelar</button>
                    <button className={`btn sm ${acao.destino === 'GANHO' ? 'venda' : 'primary'}`} disabled={acao.destino === 'PERDIDO' && !motivo} onClick={() => void confirmar()}>Confirmar</button>
                  </div>
                </div>
              )}
              <span className="small faint">Para registrar junto com uma ligação, visita ou mensagem, use “Registrar contato” na ficha.</span>
            </div>
          )}
          <Link className="btn" style={{ alignSelf: 'flex-start' }} to={`/clientes/${neg.clienteId}`}>Abrir ficha do cliente</Link>
        </div>
        <Toast msg={toast.msg} onDone={toast.clear} />
      </aside>
    </div>
  );
}
